'use client'

import { ChainType } from '@polaris/core/models'
import { useAtomValue } from 'jotai'
import { type PropsWithChildren, useMemo } from 'react'
import { useFormFilled } from '@/src/hooks/swap-form/use-form-filled'
import { isRunningAtom } from '@/src/stores/route-machine/atoms'
import {
  routeErrorsAtom,
  swapRequirementsAtom,
  toTokenAcrossChainsAtom,
} from '@/src/stores/swap/atoms'
import { ErrorInline } from '../ui/error-inline'

interface AmountOutErrorsProps {
  className?: string
}

export function AmountOutErrors({
  className,
  children,
}: PropsWithChildren<AmountOutErrorsProps>) {
  const formFilled = useFormFilled()
  const { enabled } = useAtomValue(swapRequirementsAtom)
  const routeErrors = useAtomValue(routeErrorsAtom)
  const isRunning = useAtomValue(isRunningAtom)
  const { data: toToken, error: toTokenError } = useAtomValue(
    toTokenAcrossChainsAtom,
  )

  const isNotFoundError = useMemo(() => {
    const cause = toTokenError?.cause

    if (!cause || typeof cause !== 'string') return false

    return cause.includes('not found on chain')
  }, [toTokenError?.cause])

  const isBitcoinNetwork =
    toToken?.requested_asset.chain_type === ChainType.Bitcoin

  const error = useMemo(() => {
    if (isBitcoinNetwork) {
      // Context https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
      return 'Network not currently supported'
    }

    if (routeErrors?.chainNotFound) {
      return 'Chain not found'
    }

    if (isNotFoundError) {
      return 'Unknown token'
    }

    if (routeErrors?.noRouteAvailable) {
      return 'Trade unavailable'
    }
    if (isRunning) {
      return 'A route is already in progress'
    }
    if (enabled) {
      if (formFilled) {
        if (routeErrors?.feesImpactHigh) {
          return 'More than 50% spent in fees'
        }
      }

      if (routeErrors?.priceImpactHigh) {
        return 'High price impact'
      }
    }

    return undefined
  }, [
    routeErrors,
    isRunning,
    enabled,
    formFilled,
    isNotFoundError,
    isBitcoinNetwork,
  ])

  return (
    <ErrorInline
      className={className}
      variant={isNotFoundError || isBitcoinNetwork ? 'primary' : 'danger'}
    >
      {error ?? children}
    </ErrorInline>
  )
}
